import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, Text, SimpleGrid, ThemeIcon } from '@mantine/core'
import {
  IconDeviceDesktop, IconScan, IconArrowsExchange, IconTransfer, IconFileText,
  IconTool, IconKey, IconAlertTriangle, IconTicket, IconBottle, IconPackage,
  IconClipboardList, IconUsers, IconSettings,
} from '@tabler/icons-react'
import { useAuth } from '../hooks/useAuth'
import { NAV_GROUPS, isNavItemVisible, NavItem } from '../components/layout/Layout'
import { useBreakpoint } from '../hooks/useBreakpoint'

interface HomeCard {
  navPath: string        // ใช้ match กับ NAV_GROUPS เพื่อเช็คสิทธิ์การเข้าถึงตัวเดียวกับ sidebar
  icon: any
  titleKey: string
  descKey: string
  color: string          // Mantine theme color — อิงจากโทนที่ใช้ในหน้าแดชบอร์ดทรัพย์สินเดิม
}

// ลำดับและชุด feature ตามที่ตกลงไว้ — ใช้ titleKey จาก nav.* เดิม (คำเดียวกับ sidebar)
// เพื่อไม่ให้ข้อความไม่ตรงกัน ส่วน descKey เป็นคำอธิบายสั้นๆ ใหม่ที่เพิ่มเข้ามาสำหรับหน้านี้
const HOME_CARDS: HomeCard[] = [
  { navPath: '/assets',        icon: IconDeviceDesktop, titleKey: 'nav.assets',        descKey: 'home.assetsDesc',        color: 'green' },
  { navPath: '/scan',          icon: IconScan,           titleKey: 'nav.scan',          descKey: 'home.scanDesc',          color: 'orange' },
  { navPath: '/borrow',        icon: IconArrowsExchange, titleKey: 'nav.borrow',        descKey: 'home.borrowDesc',        color: 'violet' },
  { navPath: '/transfer',      icon: IconTransfer,       titleKey: 'nav.transfer',      descKey: 'home.transferDesc',      color: 'cyan' },
  { navPath: '/handover',      icon: IconFileText,       titleKey: 'nav.handover',      descKey: 'home.handoverDesc',      color: 'indigo' },
  { navPath: '/maintenance',   icon: IconTool,           titleKey: 'nav.maintenance',   descKey: 'home.maintenanceDesc',   color: 'red' },
  { navPath: '/licenses',      icon: IconKey,            titleKey: 'nav.licenses',      descKey: 'home.licensesDesc',      color: 'grape' },
  { navPath: '/write-off',     icon: IconAlertTriangle,  titleKey: 'nav.writeoff',      descKey: 'home.writeoffDesc',      color: 'gray' },
  { navPath: '/work-orders',   icon: IconTicket,         titleKey: 'nav.workOrders',    descKey: 'home.workOrdersDesc',    color: 'pink' },
  { navPath: '/hk-orders',     icon: IconBottle,         titleKey: 'nav.hkOrders',      descKey: 'home.hkOrdersDesc',      color: 'teal' },
  { navPath: '/inventory',     icon: IconPackage,        titleKey: 'nav.inventory',     descKey: 'home.inventoryDesc',     color: 'blue' },
  { navPath: '/reports',       icon: IconClipboardList,  titleKey: 'nav.reports',       descKey: 'home.reportsDesc',       color: 'yellow' },
  { navPath: '/users',         icon: IconUsers,          titleKey: 'nav.users',         descKey: 'home.usersDesc',         color: 'lime' },
  { navPath: '/configuration', icon: IconSettings,       titleKey: 'nav.configuration', descKey: 'home.configurationDesc', color: 'dark' },
]

export default function HomePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { isMobile, isSmall } = useBreakpoint()
  const {
    roles, isSuperAdmin, featuresLoaded,
    hasPermission, hasFeature, hasPageAccess,
  } = useAuth()

  const isSuperAdminRole = isSuperAdmin || roles.includes('SUPER_ADMIN')
  const isAdminRole = roles.includes('ADMIN') || roles.includes('SUPER_ADMIN')
  const ctx = { isSuperAdminRole, isAdminRole, roles, featuresLoaded, hasPermission, hasFeature, hasPageAccess }

  // ดึง NavItem จริงจาก NAV_GROUPS ตาม path เพื่อเช็คสิทธิ์ด้วยเกณฑ์เดียวกับ sidebar เป๊ะ
  const allNavItems: NavItem[] = NAV_GROUPS.flatMap(g => g.items)
  const visibleCards = HOME_CARDS.filter((card) => {
    const navItem = allNavItems.find((i) => i.path === card.navPath)
    if (!navItem) return false
    return isNavItemVisible(navItem, ctx)
  })

  return (
    <Box p={isSmall ? 16 : 32}>
      <Box mb={isMobile ? 'md' : 'xl'}>
        <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: isSmall ? 20 : 26, fontWeight: 600, letterSpacing: '-0.3px' }}>
          {t('home.title', 'Welcome to AssetOS')}
        </Text>
        <Text size="sm" c="dimmed">{t('home.subtitle', 'Pick a module to get started')}</Text>
      </Box>

      <SimpleGrid cols={{ base: 1, xs: 2, sm: 3, lg: 4 }} spacing="md">
        {visibleCards.map((card) => (
          <Box
            key={card.navPath}
            onClick={() => navigate(card.navPath)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') navigate(card.navPath) }}
            style={{
              background: '#fff', borderRadius: 16, border: '1px solid #ebebeb',
              padding: 20, cursor: 'pointer', transition: 'all .15s',
              display: 'flex', flexDirection: 'column', gap: 10,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--brand-primary, #0d9488)'; e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,.06)' }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#ebebeb'; e.currentTarget.style.boxShadow = 'none' }}
          >
            <ThemeIcon size={44} radius={12} variant="light" color={card.color}>
              <card.icon size={24} />
            </ThemeIcon>
            <Text fw={600} size="sm" style={{ fontFamily: 'Inter, sans-serif' }}>
              {t(card.titleKey)}
            </Text>
            <Text size="xs" c="dimmed" style={{ lineHeight: 1.5 }}>
              {t(card.descKey)}
            </Text>
          </Box>
        ))}
      </SimpleGrid>
    </Box>
  )
}
